import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { proposalsApi, leadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Eye,
  Edit,
  MoreVertical,
  AlertCircle
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: Edit },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800', icon: Send },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export default function Proposals() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadProposals();
  }, [statusFilter]);

  const loadProposals = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await proposalsApi.list(params);
      setProposals(response.data);
    } catch (error) {
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async (id) => {
    try {
      await proposalsApi.submitForApproval(id);
      toast.success('Proposal submitted for approval');
      loadProposals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async (id) => {
    try {
      await proposalsApi.approve(id);
      toast.success('Proposal approved');
      loadProposals();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    try {
      await proposalsApi.reject(selectedProposal.id, rejectReason);
      toast.success('Proposal rejected');
      loadProposals();
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleSend = async (id) => {
    try {
      await proposalsApi.send(id);
      toast.success('Proposal sent to lead');
      loadProposals();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send proposal');
    }
  };

  const viewProposal = async (proposal) => {
    try {
      const response = await proposalsApi.get(proposal.id);
      setSelectedProposal(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load proposal details');
    }
  };

  const ProposalCard = ({ proposal }) => {
    const statusInfo = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusInfo.icon;

    return (
      <Card 
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => viewProposal(proposal)}
        data-testid={`proposal-card-${proposal.id}`}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold">Proposal v{proposal.version}</p>
                <p className="text-sm text-slate-500">
                  {new Date(proposal.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <span className={`status-badge ${statusInfo.color}`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusInfo.label}
            </span>
          </div>

          <p className="text-sm text-slate-600 line-clamp-2 mb-4">
            {proposal.strategy_overview || 'No overview available'}
          </p>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <div className="text-sm text-slate-500">
              {proposal.options?.length || 0} options
            </div>
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {proposal.status === 'draft' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSubmitForApproval(proposal.id)}
                  data-testid={`submit-approval-${proposal.id}`}
                >
                  Submit for Approval
                </Button>
              )}
              {proposal.status === 'approved' && (
                <Button
                  size="sm"
                  className="btn-primary"
                  onClick={() => handleSend(proposal.id)}
                  data-testid={`send-proposal-${proposal.id}`}
                >
                  <Send className="w-4 h-4 mr-1" />
                  Send
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-8" data-testid="proposals-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Proposals</h1>
          <p className="text-slate-500 mt-1">Manage and approve sales proposals</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Proposals Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-slate-100 rounded-xl animate-pulse"></div>
          ))}
        </div>
      ) : proposals.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No proposals found</p>
            <p className="text-sm text-slate-400">
              Proposals are generated from the Lead Detail page after discovery is complete
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {proposals.map(proposal => (
            <ProposalCard key={proposal.id} proposal={proposal} />
          ))}
        </div>
      )}

      {/* Proposal Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedProposal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                  Proposal v{selectedProposal.proposal?.version}
                  <span className={`status-badge ${STATUS_CONFIG[selectedProposal.proposal?.status]?.color}`}>
                    {STATUS_CONFIG[selectedProposal.proposal?.status]?.label}
                  </span>
                </DialogTitle>
              </DialogHeader>

              <div className="py-4 space-y-6">
                {/* Lead Info */}
                {selectedProposal.lead && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Prepared for</p>
                    <p className="font-semibold">{selectedProposal.lead.name}</p>
                    <p className="text-sm text-slate-600">{selectedProposal.lead.company}</p>
                  </div>
                )}

                {/* Strategy Overview */}
                <div>
                  <h4 className="font-semibold mb-2">Strategy Overview</h4>
                  <p className="text-slate-600">
                    {selectedProposal.proposal?.strategy_overview || 'No overview provided'}
                  </p>
                </div>

                {/* Options */}
                <div>
                  <h4 className="font-semibold mb-3">Pricing Options</h4>
                  <div className="space-y-4">
                    {selectedProposal.proposal?.options?.map((option, i) => (
                      <div key={i} className="p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            option.tier === 'Good' ? 'bg-slate-100 text-slate-700' :
                            option.tier === 'Better' ? 'bg-blue-100 text-blue-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {option.tier}
                          </span>
                          <span className="font-bold">
                            {option.price ? `$${option.price.toLocaleString()}` : option.price_placeholder}
                          </span>
                        </div>
                        <p className="font-medium">{option.name}</p>
                        <p className="text-sm text-slate-600 mt-1">{option.description}</p>
                        {option.deliverables && (
                          <ul className="mt-2 text-sm text-slate-600 list-disc list-inside">
                            {option.deliverables.map((d, j) => (
                              <li key={j}>{d}</li>
                            ))}
                          </ul>
                        )}
                        <p className="text-sm text-slate-500 mt-2">
                          Timeline: {option.timeline}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assumptions */}
                {selectedProposal.proposal?.assumptions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Assumptions</h4>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                      {selectedProposal.proposal.assumptions.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Next Steps */}
                {selectedProposal.proposal?.next_steps?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Next Steps</h4>
                    <ul className="list-decimal list-inside text-slate-600 space-y-1">
                      {selectedProposal.proposal.next_steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                {selectedProposal.proposal?.status === 'pending_approval' && isAdmin() && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectModal(true)}
                      className="text-red-600 hover:bg-red-50"
                      data-testid="reject-proposal-btn"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      className="btn-primary"
                      onClick={() => handleApprove(selectedProposal.proposal.id)}
                      data-testid="approve-proposal-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
                {selectedProposal.proposal?.status === 'approved' && (
                  <Button
                    className="btn-primary"
                    onClick={() => handleSend(selectedProposal.proposal.id)}
                    data-testid="send-proposal-modal-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to Lead
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Proposal</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for rejection</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Provide feedback for why this proposal is being rejected..."
              className="mt-2"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              data-testid="confirm-reject-btn"
            >
              Reject Proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
